/**
 * ScrollToTop - Floating button to scroll to top of page
 * 
 * Dashboard3 styled scroll to top button with glassmorphism effects.
 * Appears after scrolling down 300px.
 * 
 * @example
 * ```tsx
 * <ScrollToTop />
 * ```
 */

"use client";

import { ChevronUpIcon } from "@chakra-ui/icons";
import { AnimatePresence, motion } from "framer-motion";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { IconButton } from "./IconButton";
import { useThemeTokens } from "@/lib/theme/tokens";
import { useInteractiveTokens } from "@/lib/theme/tokens/interactive";
import {
  applyScrollPosition,
  getRouteScrollKey,
  getScrollableContainer,
  readCurrentScrollPosition,
  readRouteScrollPosition,
  saveRouteScrollPosition,
  scrollRouteToTop,
  shouldRestoreRouteScroll,
} from "@/lib/utils/routeScrollRestoration";

const RESTORE_RETRY_MS = [0, 50, 150];

export const ScrollToTop = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isStickyActionBarActive, setIsStickyActionBarActive] = useState(false);
  const pathname = usePathname();
  const theme = useThemeTokens();
  const interactive = useInteractiveTokens();
  const previousKeyRef = useRef<string | null>(null);
  const previousPathnameRef = useRef<string | null>(null);
  const poppedRef = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined" || !("scrollRestoration" in history)) {
      return;
    }
    const previous = history.scrollRestoration;
    history.scrollRestoration = "manual";
    return () => {
      history.scrollRestoration = previous;
    };
  }, []);

  useEffect(() => {
    const markPopped = () => {
      poppedRef.current = true;
    };
    window.addEventListener("popstate", markPopped);
    return () => window.removeEventListener("popstate", markPopped);
  }, []);

  useEffect(() => {
    const persist = () => {
      const key = previousKeyRef.current ?? getRouteScrollKey(
        window.location.pathname,
        window.location.search,
      );
      saveRouteScrollPosition(key);
    };

    const onScroll = () => persist();
    window.addEventListener("scroll", onScroll, { passive: true });
    const container = getScrollableContainer();
    container?.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("pagehide", persist);

    return () => {
      window.removeEventListener("scroll", onScroll);
      container?.removeEventListener("scroll", onScroll);
      window.removeEventListener("pagehide", persist);
    };
  }, [pathname]);

  useEffect(() => {
    const nextKey = getRouteScrollKey(pathname, window.location.search);
    const fromPathname = previousPathnameRef.current ?? "";
    if (previousKeyRef.current) {
      saveRouteScrollPosition(
        previousKeyRef.current,
        readCurrentScrollPosition(),
      );
    }

    const restore =
      shouldRestoreRouteScroll({
        popped: poppedRef.current,
        fromPathname,
        toPathname: pathname,
      }) && readRouteScrollPosition(nextKey);

    poppedRef.current = false;
    previousKeyRef.current = nextKey;
    previousPathnameRef.current = pathname;

    const timeouts = (restore ? RESTORE_RETRY_MS : [0]).map((delay) =>
      window.setTimeout(() => {
        if (restore) {
          applyScrollPosition(restore);
          return;
        }
        scrollRouteToTop();
      }, delay),
    );

    return () => {
      timeouts.forEach((timeoutId) => window.clearTimeout(timeoutId));
    };
  }, [pathname]);

  useEffect(() => {
    const toggleVisibility = () => {
      const scrollableContainer = getScrollableContainer();
      const scrollY = scrollableContainer
        ? scrollableContainer.scrollTop
        : window.scrollY;

      if (scrollY > 300) setIsVisible(true);
      else setIsVisible(false);
    };

    const scrollableContainer = getScrollableContainer();
    toggleVisibility();

    if (scrollableContainer) {
      scrollableContainer.addEventListener("scroll", toggleVisibility);
      return () =>
        scrollableContainer.removeEventListener("scroll", toggleVisibility);
    }

    window.addEventListener("scroll", toggleVisibility);
    return () => window.removeEventListener("scroll", toggleVisibility);
  }, [pathname]);

  useEffect(() => {
    const root = document.documentElement;

    const syncStickyActionBar = () => {
      setIsStickyActionBarActive(root.dataset.stickyActionBar === "true");
    };

    syncStickyActionBar();

    const observer = new MutationObserver(syncStickyActionBar);
    observer.observe(root, {
      attributes: true,
      attributeFilter: ["data-sticky-action-bar"],
    });

    return () => observer.disconnect();
  }, [pathname]);

  const scrollToTop = () => {
    const scrollableContainer = getScrollableContainer();

    if (scrollableContainer) {
      scrollableContainer.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } else {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  return (
    <AnimatePresence>
      {isVisible && !isStickyActionBarActive && (
        <motion.div
          exit={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          initial={{ opacity: 0, y: 50 }}
          style={{
            zIndex: 1000,
            right: "30px",
            bottom: "30px",
            position: "fixed",
          }}
        >
          <IconButton
            aria-label="Scroll to top"
            icon={<ChevronUpIcon boxSize={5} />}
            size="md"
            onClick={scrollToTop}
            bg={theme.accent.primary}
            color="white"
            borderRadius="full"
            boxShadow={theme.shadow.card}
            sx={{
              "@media (hover: hover)": {
                "&:hover": {
                  bg: interactive.button.primary.hover,
                  transform: "scale(1.1)",
                },
              },
              "@media (hover: none) and (pointer: coarse)": {
                "&:active": {
                  bg: interactive.button.primary.active,
                },
              },
            }}
            _focus={{
              boxShadow: theme.focusRing,
            }}
            transition="all 0.18s ease-out"
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

